/**
 * 动态配置加载器
 * 从环境变量加载配置，无需额外的配置文件
 */

export interface AppConfig {
  // 基础配置
  projectName: string
  mode: 'development' | 'production' | 'test'
  title: string
  apiBaseUrl: string

  // Logo 配置
  logoUrl?: string
  logoText?: string
  horizontalLogoUrl?: string

  // 地区配置
  currency: string
  defaultLang: string
  timezone: string

  // 支持的语言列表
  supportedLangs: string[]

  // 主题配置
  theme: {
    primaryColor?: string
    secondaryColor?: string
    borderRadius?: string
    mode?: 'light' | 'dark' | 'auto'
  }

  // 功能开关
  features: {
    enableAuth: boolean
    enableAnalytics: boolean
    enableNotification: boolean
    enableMultiLanguage: boolean
    enableDarkMode: boolean
    [key: string]: boolean
  }

  // 第三方服务配置
  services: {
    analyticsId?: string
    searchApiKey?: string
    mapsApiKey?: string
    cdnUrl?: string
    [key: string]: string | undefined
  }

  // 联系信息
  contact: {
    supportEmail?: string
    phoneNumber?: string
    address?: string
    [key: string]: string | undefined
  }

  // 所有环境变量
  env: Record<string, string>
}

/**
 * 解析布尔值环境变量
 */
const parseBool = (value: string | undefined, defaultValue = false): boolean => {
  if (!value) return defaultValue
  return value.toLowerCase() === 'true'
}

/**
 * 解析数组环境变量（逗号分隔）
 */
const parseArray = (value: string | undefined, defaultValue: string[] = []): string[] => {
  if (!value) return defaultValue
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

/**
 * 从环境变量加载配置
 */
export const loadConfig = (): AppConfig => {
  const env: Record<string, string> = {}

  // 收集所有 VITE_ 开头的环境变量
  for (const key in import.meta.env) {
    if (key.startsWith('VITE_')) {
      env[key] = import.meta.env[key] as string
    }
  }

  const projectName = import.meta.env.VITE_PROJECT_NAME || 'default'
  const mode = (import.meta.env.MODE || 'development') as AppConfig['mode']

  return {
    // 基础配置
    projectName,
    mode,
    title: import.meta.env.VITE_APP_TITLE || `${projectName} Application`,
    apiBaseUrl: import.meta.env.VITE_API_BASE_URL || '/api',

    // Logo 配置
    logoUrl: import.meta.env.VITE_LOGO_URL,
    logoText: import.meta.env.VITE_LOGO_TEXT,
    horizontalLogoUrl: import.meta.env.VITE_HORIZONTAL_LOGO_URL,

    // 地区配置
    currency: import.meta.env.VITE_CURRENCY || 'USD',
    defaultLang: import.meta.env.VITE_DEFAULT_LANG || 'en-US',
    timezone: import.meta.env.VITE_TIMEZONE || 'UTC',

    // 支持的语言列表
    supportedLangs: parseArray(import.meta.env.VITE_SUPPORTED_LANGS, [
      import.meta.env.VITE_DEFAULT_LANG || 'en-US',
    ]),

    // 主题配置
    theme: {
      primaryColor: import.meta.env.VITE_PRIMARY_COLOR,
      secondaryColor: import.meta.env.VITE_SECONDARY_COLOR,
      borderRadius: import.meta.env.VITE_BORDER_RADIUS,
      mode: (import.meta.env.VITE_THEME_MODE as AppConfig['theme']['mode']) || 'auto',
    },

    // 功能开关
    features: {
      enableAuth: parseBool(import.meta.env.VITE_ENABLE_AUTH, true),
      enableAnalytics: parseBool(import.meta.env.VITE_ENABLE_ANALYTICS, false),
      enableNotification: parseBool(import.meta.env.VITE_ENABLE_NOTIFICATION, true),
      enableMultiLanguage: parseBool(import.meta.env.VITE_ENABLE_MULTI_LANGUAGE, true),
      enableDarkMode: parseBool(import.meta.env.VITE_ENABLE_DARK_MODE, true),
    },

    // 第三方服务配置
    services: {
      analyticsId: import.meta.env.VITE_ANALYTICS_ID,
      searchApiKey: import.meta.env.VITE_SEARCH_API_KEY,
      mapsApiKey: import.meta.env.VITE_MAPS_API_KEY,
      cdnUrl: import.meta.env.VITE_CDN_URL,
    },

    // 联系信息
    contact: {
      supportEmail: import.meta.env.VITE_SUPPORT_EMAIL,
      phoneNumber: import.meta.env.VITE_PHONE_NUMBER,
      address: import.meta.env.VITE_ADDRESS,
    },

    // 所有环境变量
    env,
  }
}

/**
 * 配置缓存
 */
let cachedConfig: AppConfig | null = null

/**
 * 获取应用配置（带缓存）
 */
export const getAppConfig = (): AppConfig => {

  if (cachedConfig) {
    return cachedConfig
  }

  cachedConfig = loadConfig()
  return cachedConfig
}

/**
 * 获取项目名称
 */
export const getProjectName = (): string => {
  return import.meta.env.VITE_PROJECT_NAME || 'default'
}

/**
 * 重置配置缓存（主要用于测试）
 */
export const resetConfigCache = (): void => {
  cachedConfig = null
}
